import { useQuery } from "@tanstack/react-query";
import axiosInstance from "@/lib/axios";
import { leadsListCountEndpoint } from "@/config/endpoints";
import { queryClient, transientRetry } from "@/lib/queryClient";

/**
 * Filter-aware total count for the lead list. Fires in parallel with
 * `useLeads` so pagination state populates without blocking the visible
 * cards. Same filter object as `useLeads(filters)` — server uses the same
 * filter-resolution path so the count matches what the list shows.
 */
export function useLeadsListCount(filters?: any) {
  const query = useQuery<number>({
    queryKey: ["leads-list-count", filters],
    queryFn: async () => {
      const res = await axiosInstance.get(leadsListCountEndpoint, {
        params: filters,
      });
      return Number(res.data?.total) || 0;
    },
    staleTime: 60 * 1000,
    // Back on the tab → re-ask once stale, like the custom report beside it
    // (useReportResult): two screens compared side by side must not show
    // numbers fetched at different moments under the same filters.
    refetchOnWindowFocus: true,
    ...transientRetry,
  });

  return {
    total: query.data ?? 0,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

export const refetchLeadsListCount = () => {
  queryClient.invalidateQueries({ queryKey: ["leads-list-count"] });
};
